import React, { FC, MouseEvent, useCallback } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import User from "@mapmycustomers/shared/types/User";
import { LoadingSpinner } from "@mapmycustomers/ui";

import localSettings from "@app/config/LocalSettings";
import { showUserDrillDown } from "@app/scene/dashboard/store/stackRank/actions";
import { MetricData } from "@app/scene/dashboard/types/PerformanceData";
import formatSeconds from "@app/scene/dashboard/utils/formatSeconds";

import styles from "./MetricValue.module.scss";

interface Props {
  loading?: boolean;
  metric: IField;
  onClick?: (userIds?: User["id"][]) => void;
  performance?: MetricData;
  userIds?: User["id"][];
}

const MetricValue: FC<Props> = ({ loading, metric, onClick, performance, userIds }) => {
  const intl = useIntl();

  const handleClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      return onClick?.(userIds);
    },
    [userIds, onClick]
  );

  if (!performance) {
    return loading ? (
      <div className={styles.container}>
        <LoadingSpinner mini />
      </div>
    ) : null;
  }

  const metricName = metric.name as LeaderboardMetricFieldName;
  const formattedValue =
    metricName === LeaderboardMetricFieldName.REVENUE_CLOSED_LOST ||
    metricName === LeaderboardMetricFieldName.REVENUE_CLOSED_WON ||
    metricName === LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE
      ? intl.formatNumber(performance.value, {
          currency: localSettings.getCurrencyCode(),
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
          style: "currency",
        })
      : metricName === LeaderboardMetricFieldName.VERIFIED_FACE_TIME ||
        metricName === LeaderboardMetricFieldName.TOTAL_FACE_TIME
      ? formatSeconds(intl, performance.value)
      : intl.formatNumber(performance.value);

  return (
    <Tooltip
      title={intl.formatMessage({
        id: "dashboard.sidebar.metricValue.tooltip",
        defaultMessage: "View All",
        description: "Tooltip of the clickable metric value",
      })}
    >
      <div className={styles.container} onClick={handleClick}>
        {formattedValue}
      </div>
    </Tooltip>
  );
};

const mapDispatchToProps = {
  onClick: showUserDrillDown.request,
};

export default connect(null, mapDispatchToProps)(MetricValue);
